/**
 * Shared card internals — React (web).
 *
 * Composed by the five standalone cards. All styling references the shared
 * tokens in `./shared`, which reference the colour/typography/spacing/radius
 * elements. No raw hex or pixel literals in this file.
 *
 * Internal to the card family — not re-exported from the package barrel.
 */

/// <reference lib="dom" />

import React, { useCallback, useEffect, useRef } from 'react';
import { spacing } from '../../elements/spacing';
import { shadows } from '../../elements/shadows';
import type { IconName } from '../../elements/icons';
import type { TypeStyle } from '../../elements/typography';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import {
  useAnalyticsCallback,
  useDelegatedAnalyticsAction,
} from '../../analytics/AnalyticsActionContext';
import { Icon } from '../Icon/Icon.web';
import { Avatar } from '../Avatar/Avatar.web';
import {
  type CardColors,
  type CardDetail,
  CARD_DETAIL_GLYPH_SIZE,
  CARD_GAP,
  CARD_MIN_TOUCH_TARGET,
  CARD_PILL_RADIUS,
  CARD_RADIUS,
  CARD_TYPOGRAPHY as TYPO,
} from './shared';

/** Marker attribute carried by every custom focusable control in the family. */
export const CARD_CONTROL_ATTR = 'data-ds-card-control';

export const typo = (t: TypeStyle): React.CSSProperties => ({
  fontFamily: t.fontFamily,
  fontWeight: t.fontWeight,
  fontSize: t.fontSize,
  lineHeight: `${t.lineHeight}px`,
  letterSpacing: t.letterSpacing,
});

/** Figma "Shadow 300" — the standard floating card shadow. */
const CARD_SHADOW = `${shadows.shadow300.offset.width}px ${shadows.shadow300.offset.height}px ${shadows.shadow300.radius}px ${shadows.shadow300.spread}px ${shadows.shadow300.color}`;

const FOCUS_STYLE_ID = 'ds-card-focus';

/** Inject the family's shared `:focus-visible` outline once per document. */
export function useCardFocusStyle(): void {
  useEffect(() => {
    ensureFocusVisibleStyle(FOCUS_STYLE_ID, [`[${CARD_CONTROL_ATTR}]`]);
  }, []);
}

export function CardShell({
  colors: c,
  paddingX,
  paddingY,
  paddingBottom,
  shadow,
  children,
  band,
  bandSurface,
  fillHeight,
  radius: cornerRadius,
  testId,
  ariaLabel,
}: {
  colors: CardColors;
  paddingX: number;
  paddingY: number;
  /** Overrides the bottom inset only; defaults to `paddingY`. */
  paddingBottom?: number;
  shadow?: boolean;
  children?: React.ReactNode;
  /**
   * Content for a band along the card's foot, reaching all three of its edges.
   * The shell keeps ownership of it because the effect is the card's own
   * geometry: the band is clipped to the shared radius rather than drawing
   * corners of its own, so it cannot fall out of step with the card's shape.
   */
  band?: React.ReactNode;
  bandSurface?: string;
  /** Fill the container's height instead of hugging the content. */
  fillHeight?: boolean;
  /**
   * Corner radius override; absent keeps the canonical card radius. The band
   * and body are clipped to it, so they follow without corners of their own.
   */
  radius?: number;
  testId?: string;
  ariaLabel?: string;
}) {
  const banded = band !== undefined;
  const style: React.CSSProperties = {
    boxSizing: 'border-box',
    width: '100%',
    ...(fillHeight === true ? { height: '100%' } : {}),
    // A band reaches the card's edges, so the shell drops its own insets and
    // the body re-applies them one level in. The row gap goes with them: the
    // band meets the body it follows rather than floating a strip of card
    // surface above itself.
    ...(banded
      ? { gap: 0 }
      : {
          paddingLeft: paddingX,
          paddingRight: paddingX,
          paddingTop: paddingY,
          paddingBottom: paddingBottom ?? paddingY,
          gap: CARD_GAP,
        }),
    borderRadius: cornerRadius ?? CARD_RADIUS,
    border: `1px solid ${c.border}`,
    backgroundColor: c.surface,
    display: 'flex',
    flexDirection: 'column',
    overflow: shadow ? 'visible' : 'hidden',
    boxShadow: shadow ? CARD_SHADOW : undefined,
  };
  if (!banded) {
    return (
      <div style={style} data-testid={testId} aria-label={ariaLabel}>
        {children}
      </div>
    );
  }
  return (
    <div style={style} data-testid={testId} aria-label={ariaLabel}>
      {children === undefined ? null : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: CARD_GAP,
            paddingLeft: paddingX,
            paddingRight: paddingX,
            paddingTop: paddingY,
            paddingBottom: paddingBottom ?? paddingY,
          }}
        >
          {children}
        </div>
      )}
      <div
        style={{
          backgroundColor: bandSurface,
          paddingLeft: paddingX,
          paddingRight: paddingX,
          paddingTop: paddingY,
          paddingBottom: paddingY,
        }}
      >
        {band}
      </div>
    </div>
  );
}

export function Eyebrow({ value, color }: { value?: string; color: string }) {
  if (!value) return null;
  return <span style={{ ...typo(TYPO.eyebrow), color }}>{value}</span>;
}

export function DetailRow({ detail, colors: c }: { detail: CardDetail; colors: CardColors }) {
  return (
    <div
      style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: spacing.s }}
    >
      {detail.avatar || detail.iconName ? (
        <div style={{ display: 'flex', alignItems: 'center', height: TYPO.detail.lineHeight }}>
          {detail.avatar ? (
            <Avatar
              imageUri={detail.avatar.imageUri}
              label={detail.avatar.label}
              size={CARD_DETAIL_GLYPH_SIZE}
              fallback={
                detail.iconName ? (
                  <Icon
                    name={detail.iconName}
                    size={CARD_DETAIL_GLYPH_SIZE}
                    color={c.icon}
                    aria-hidden
                  />
                ) : undefined
              }
            />
          ) : detail.iconName ? (
            <Icon name={detail.iconName} size={CARD_DETAIL_GLYPH_SIZE} color={c.icon} aria-hidden />
          ) : null}
        </div>
      ) : null}
      <span style={{ ...typo(TYPO.detail), color: c.title, flex: 1 }}>{detail.value}</span>
    </div>
  );
}

export function Pill({
  actionId = 'ui.card-pill.activate',
  label,
  onPress,
  background,
  color,
  iconName,
  testId,
}: {
  actionId?: string;
  label: string;
  onPress?: () => void;
  background: string;
  color: string;
  iconName?: IconName;
  testId?: string;
}) {
  const style: React.CSSProperties = {
    minHeight: CARD_MIN_TOUCH_TARGET,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.s,
    padding: `0 ${spacing.lg}px`,
    borderRadius: CARD_PILL_RADIUS,
    backgroundColor: background,
    color,
    border: 'none',
    cursor: 'pointer',
    ...typo(TYPO.title),
    fontSize: TYPO.body.fontSize,
  };
  return (
    <button
      data-analytics-action={actionId}
      type="button"
      onClick={onPress}
      aria-label={label}
      data-testid={testId}
      {...{ [CARD_CONTROL_ATTR]: true }}
      style={style}
    >
      {iconName && <Icon name={iconName} size={CARD_DETAIL_GLYPH_SIZE} color={color} aria-hidden />}
      <span>{label}</span>
    </button>
  );
}

export function TextAction({
  actionId = 'ui.card-text-action.activate',
  label,
  onPress,
  color,
  testId,
}: {
  actionId?: string;
  label: string;
  onPress?: () => void;
  color: string;
  testId?: string;
}) {
  return (
    <button
      data-analytics-action={actionId}
      type="button"
      onClick={onPress}
      aria-label={label}
      data-testid={testId}
      {...{ [CARD_CONTROL_ATTR]: true }}
      style={{
        width: '100%',
        minHeight: CARD_MIN_TOUCH_TARGET,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color,
        ...typo(TYPO.title),
      }}
    >
      {label}
    </button>
  );
}

/** Single-select radio group with roving tabindex + arrow keys (ADR-025 Rule 1). */
interface RovingItem {
  id: string;
  disabled?: boolean;
  ariaLabel?: string;
}
export function RovingSelect<T extends RovingItem>({
  actionId,
  items,
  selectedId,
  onSelect,
  ariaLabel,
  groupStyle,
  itemStyle,
  renderContent,
  testIdPrefix,
}: {
  actionId?: string;
  items: T[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  ariaLabel?: string;
  groupStyle: React.CSSProperties;
  itemStyle: (item: T, selected: boolean) => React.CSSProperties;
  renderContent: (item: T, selected: boolean) => React.ReactNode;
  testIdPrefix?: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const delegatedActionId = useDelegatedAnalyticsAction(actionId);
  const handleKeyboardSelect = useAnalyticsCallback(actionId, onSelect, false, 'keyboard');
  const enabledIndices = items.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0);
  const selectedIndex = selectedId ? items.findIndex((it) => it.id === selectedId) : -1;
  const focusableIndex = selectedIndex >= 0 ? selectedIndex : (enabledIndices[0] ?? 0);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const current = refs.current.findIndex((r) => r === document.activeElement);
      if (current === -1) return;
      const pos = enabledIndices.indexOf(current);
      if (pos === -1) return;
      let next: number | undefined;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        next = enabledIndices[(pos + 1) % enabledIndices.length];
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        next = enabledIndices[(pos - 1 + enabledIndices.length) % enabledIndices.length];
      }
      if (next !== undefined) {
        refs.current[next]?.focus();
        handleKeyboardSelect?.(items[next].id);
      }
    },
    [enabledIndices, handleKeyboardSelect, items],
  );

  return (
    <div role="radiogroup" aria-label={ariaLabel} style={groupStyle} onKeyDown={handleKeyDown}>
      {items.map((item, index) => {
        const selected = item.id === selectedId;
        return (
          <button
            data-analytics-action={delegatedActionId}
            key={item.id}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={item.ariaLabel}
            aria-disabled={item.disabled || undefined}
            tabIndex={index === focusableIndex ? 0 : -1}
            onClick={() => !item.disabled && onSelect?.(item.id)}
            data-testid={testIdPrefix ? `${testIdPrefix}-${item.id}` : undefined}
            {...{ [CARD_CONTROL_ATTR]: true }}
            style={itemStyle(item, selected)}
          >
            {renderContent(item, selected)}
          </button>
        );
      })}
    </div>
  );
}
